import { api } from "@/shared/api";

const BASE = "/tenant/biz-advisor";

/** 诊断摘要(列表 + 发起回执)。 */
export interface DiagnosisSummary {
  id: number;
  brand: string | null;
  industry: string | null;
  overallScore: number | null;
  grade: string | null;
  topLeverage: string | null;
  status: string; // RUNNING | SUCCESS | FAILED
  createdAt: string;
}

/** 诊断详情(轮询)。 */
export interface DiagnosisDetail extends DiagnosisSummary {
  trafficScore: number | null;
  conversionScore: number | null;
  aovScore: number | null;
  repurchaseScore: number | null;
  viralScore: number | null;
  errorMessage: string | null;
  reportJson: string | null;
}

/** 单乘子诊断(ai 侧 snake_case)。 */
export interface MultiplierScore {
  key: string; // traffic | conversion | aov | repurchase | viral
  label: string;
  score: number;
  grade: string;
  data_sufficiency: string; // ok | low
  gap: string;
  evidence: string[];
  actions: string[];
  /** C2:较上次 SUCCESS 的分差(首诊/旧报告缺位)。 */
  delta?: number | null;
  /** C4:较行业水位(above|near|below;缺数据乘子不打标 → "")。 */
  vs_benchmark?: string;
}

/** 文本执行物(仅最高杠杆且文本类才有)。 */
export interface PlayArtifact {
  kind: string; // objection_script | bundle | crosssell_copy | recall_copy
  title: string;
  content: string;
}

/** 一张打法卡。 */
export interface Play {
  multiplier: string; // traffic | conversion | aov | repurchase | viral
  label: string;
  is_top_leverage: boolean;
  title: string;
  tactic: string;
  steps: string[];
  rationale: string;
  suggested_engine: string; // poster | video | cs_kb | copy | none
  engine_brief: string;
  artifacts: PlayArtifact[];
}

/** 攻:增长作战地图。 */
export interface BattlePlan {
  headline: string;
  plays: Play[];
  battlefield: unknown[]; // 战场层占位,本期恒空
}

/** 解析后的 GrowthDiagnosis(ai 侧 snake_case)。 */
export interface GrowthDiagnosis {
  brand: string;
  industry: string;
  overall_score: number;
  grade: string;
  multipliers: MultiplierScore[];
  top_leverage: { multiplier: string; label: string; score: number; reason: string };
  summary: string;
  honest_notes: string[];
  battle_plan?: BattlePlan | null; // 攻:作战地图(B2,向后兼容,老报告可能无)
  /** C2:总分较上次 delta + 上次测量时间(首诊/旧报告缺位)。 */
  overall_delta?: number | null;
  previous_measured_at?: string;
  /** C4:行业基准命中的行业大类桶(内置静态水位表 v1)。 */
  benchmark_bucket?: string;
  /** C+:本次诊断参照回显(手填竞品/标杆)。 */
  competitors?: string[];
  benchmark?: string;
}

/** 派单记录(后端 DispatchDto)。 */
export interface DispatchDto {
  id: number;
  playKey: string;
  engine: string;
  status: string; // DISPATCHED | SAVED | REMEASURED ...
  baselineScore: number | null;
  resultScore: number | null;
  kbDocId: number | null;
}

/** 复测单乘子 delta(后端 RemeasureResultDto.Delta)。 */
export interface RemeasureDelta {
  playKey: string;
  before: number | null;
  after: number | null;
  delta: number | null;
}

/** 复测对比结果(后端 RemeasureResultDto)。 */
export interface RemeasureResult {
  newDiagnosisId: number;
  deltas: RemeasureDelta[];
}

/** 文本入库 docType:cs_kb→qa、copy→product。 */
export type KbDocType = "qa" | "product";

/** 发起诊断入参(E,反馈#8):品牌 + 手填增强(行业/竞品/标杆,均可空)。 */
export interface DiagnoseInputs {
  brandHint?: string;
  industry?: string;
  competitors?: string[];
  benchmark?: string;
}

export const bizAdvisorService = {
  /** 发起一次诊断(异步);返回 RUNNING 回执(含 id)。手填增强空则采集侧派生/不加约束兜底。 */
  createDiagnosis: (input: DiagnoseInputs = {}) =>
    api.post<DiagnosisSummary>(`${BASE}/diagnoses`, {
      brandHint: input.brandHint ?? "",
      industry: input.industry ?? "",
      competitors: input.competitors ?? [],
      benchmark: input.benchmark ?? "",
    }),
  /** 最近诊断列表。 */
  listDiagnoses: (limit = 20) => api.get<DiagnosisSummary[]>(`${BASE}/diagnoses?limit=${limit}`),
  /** 诊断详情(轮询用)。 */
  getDiagnosis: (id: number) => api.get<DiagnosisDetail>(`${BASE}/diagnoses/${id}`),
  /** B3 视觉类派单(记录 provenance + baseline,深链跳引擎前调);需 MANAGER+。 */
  dispatch: (diagnosisId: number, body: { playKey: string; engine: string; brief: string }) =>
    api.post<DispatchDto>(`${BASE}/diagnoses/${diagnosisId}/dispatch`, body),
  /** B3 文本类入库(确认层确认后调;复用 KB ingest 正门);需 MANAGER+。 */
  dispatchKb: (
    diagnosisId: number,
    body: {
      playKey: string;
      engine: string;
      docType: KbDocType;
      title: string;
      content: string;
      tags: string[];
    }
  ) => api.post<DispatchDto>(`${BASE}/diagnoses/${diagnosisId}/dispatch-kb`, body),
  /** B3 列某诊断的派单(复测对比用)。 */
  listDispatches: (diagnosisId: number) =>
    api.get<DispatchDto[]>(`${BASE}/diagnoses/${diagnosisId}/dispatches`),
  /** B3 复测:用新诊断算被派乘子 delta。 */
  remeasureResult: (diagnosisId: number, newDiagnosisId: number) =>
    api.post<RemeasureResult>(`${BASE}/diagnoses/${diagnosisId}/remeasure-result`, {
      newDiagnosisId,
    }),
};

/** 解析 report_json 字符串;失败返 null。 */
export function parseDiagnosis(json: string | null): GrowthDiagnosis | null {
  if (!json) return null;
  try {
    return JSON.parse(json) as GrowthDiagnosis;
  } catch {
    return null;
  }
}
